// Default config and business shapes (API-only; no mock persistence).
// DB pattern: null = use product/business/calendar resolution defaults until the user sets an explicit value.

/** Display name used when business name is missing (e.g. welcome message fallback in store on 404). */
export const FALLBACK_BUSINESS_NAME = 'My Business';

/** Shape used when business is not loaded yet (e.g. config panel before fetch). */
const EMPTY_BUSINESS = { id: '', name: '' };

/** Placeholder in welcome message template; replaced with actual business name in store (404 merge). */
const WELCOME_MESSAGE_PLACEHOLDER = '{{businessName}}';

/**
 * Default welcome message template. Use getDefaultWelcomeMessage(businessName) to get the message
 * with business name substituted (e.g. in store when building config on 404).
 */
const DEFAULT_WELCOME_MESSAGE_TEMPLATE = `Hi,
Welcome to {{businessName}}.
How can I help you today?`;

/**
 * Default welcome buttons shown under the welcome message.
 */
const DEFAULT_WELCOME_BUTTONS = [
  { label: 'Book an appointment', message: "I'd like to book an appointment" },
  { label: 'Ask about services', message: 'What services do you offer?' },
  { label: 'Query opening hours', message: 'What are your hours?' },
];

/**
 * Build the default welcome message with business name inserted.
 * @param {string} [businessName] - Business display name (e.g. from platform API); falls back to FALLBACK_BUSINESS_NAME
 * @returns {string}
 */
function getDefaultWelcomeMessage(businessName) {
  const name = businessName || FALLBACK_BUSINESS_NAME;
  return DEFAULT_WELCOME_MESSAGE_TEMPLATE.split(WELCOME_MESSAGE_PLACEHOLDER).join(name);
}

/**
 * Product default copy for lead capture when DB has null (same string server/widget use for resolution).
 */
const DEFAULT_LEAD_CAPTURE_TEXT = 'Details are shared so we can follow up, we will call you back as soon as we are able to, usually within 24 hours';
const LEAD_INTENT_GENERAL = 'General inquiry';
const DEFAULT_LEAD_DATA_ITEMS = 'Name, Phone number, Email(Optional), Reason for inquiry';
const DEFAULT_LEAD_MESSAGE_TO_VISITOR = DEFAULT_LEAD_CAPTURE_TEXT;
const NEW_LEAD_INTENT_PLACEHOLDER = 'Add intent here';
const NEW_LEAD_DATA_ITEMS_PLACEHOLDER = 'Name, Phone number, Email(Optional), Reason for inquiry';

/** Floating button label when `button_text` is null in DB. */
export const DEFAULT_BUTTON_TEXT = 'Contact / FAQ';

/** Picker fallback when business brand color is unavailable. */
export const DEFAULT_BRAND_COLOR_FALLBACK = '#3B82F6';

/** Default widget chrome text color when `text_color` is null in DB. */
export const DEFAULT_TEXT_COLOR = '#333333';

/** Default auto-popup delay (seconds) when `auto_popup_delay` is null in DB. */
export const DEFAULT_AUTO_POPUP_DELAY_SECONDS = 5;

/**
 * In-memory / first-persist shape: optional fields are null so the API stores NULL until customized.
 */
const DEFAULT_CONFIG = {
  id: '',
  business_id: '',
  widget_title: null,
  faq_and_guidance: null,
  welcome_message: null,
  brand_color: null,
  text_color: null,
  logo_url: null,
  actions: null,
  auto_popup_enabled: null,
  auto_popup_delay: null,
  welcome_buttons: null,
  lead_intent_data: null,
  lead_capture_text: null,
  button_text: null,
  include_contact_info: null,
  include_services: null,
  include_products: null,
  include_booking_policies: null,
  include_terms_of_service: null,
  guidance_urls: null,
};

/**
 * Paths to edit guidance sources in the app (Angular/settings). Used for "edit" links next to checkboxes.
 * Values can be a path string or { path, queryParams } so Angular receives query params separately and
 * can merge them (e.g. with section=website-widgets) without producing a malformed URL.
 */
const GUIDANCE_EDIT_PATHS = {
  contact_info: '/app/settings/business',
  services: '/app/settings/services',
  products: '/app/settings/products',
  booking_policies: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
  terms_of_service: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
};

/** Calendar settings → General availability (same as Vuetage `navigateTo('app.calendar_settings', { tab: 'GeneralAvailability' })`). */
const CALENDAR_GENERAL_AVAILABILITY_SETTINGS = {
  path: '/app/settings/calendar_settings',
  queryParams: { tab: 'GeneralAvailability' },
};

export {
  DEFAULT_CONFIG,
  DEFAULT_LEAD_CAPTURE_TEXT,
  LEAD_INTENT_GENERAL,
  DEFAULT_LEAD_DATA_ITEMS,
  DEFAULT_LEAD_MESSAGE_TO_VISITOR,
  NEW_LEAD_INTENT_PLACEHOLDER,
  NEW_LEAD_DATA_ITEMS_PLACEHOLDER,
  EMPTY_BUSINESS,
  getDefaultWelcomeMessage,
  DEFAULT_WELCOME_BUTTONS,
  GUIDANCE_EDIT_PATHS,
  CALENDAR_GENERAL_AVAILABILITY_SETTINGS,
};
